import { forwardRef, useImperativeHandle, useRef } from 'react';
import FlowWizard, { FlowWizardHandle } from '@shared/components/form/FlowWizard';
import { ActivityName } from 'app/_modules/core/types';
import { useMeasurementFlow, EActivitySubmitSteps } from 'app/_modules/core/hooks/useMeasurementFlow';

export { EActivitySubmitSteps };

const MeasurementFlow = forwardRef<FlowWizardHandle, {
  activityName: ActivityName;
  activityId: string;
  initialStep?: number;
  forceReset?: boolean;
  isRemeasurement?: boolean;
  autoStartWebSocket?: boolean;
  hasDeviceError?: boolean;
  onStepChange?: (prevStep: number, currentStep: number) => void;
  onWaitingStateChange?: (isWaiting: boolean) => void;
}>(({
  activityName,
  activityId,
  initialStep = 0,
  forceReset = false,
  isRemeasurement = false,
  autoStartWebSocket = false,
  hasDeviceError = false,
  onStepChange,
  onWaitingStateChange,
}, ref) => {
  const wizardRef = useRef<FlowWizardHandle>(null);

  const { steps, webSocketConfig, backButton, finishButton, customAction, resolvedInitialStep, handleFinish, onStepChange: setCurrentStep } =
    useMeasurementFlow({
      activityName,
      activityId,
      initialStep,
      forceReset,
      isRemeasurement,
      autoStartWebSocket,
      hasDeviceError,
      goToStep: (index) => wizardRef.current?.goToStep(index),
      startWebSocketListening: () => wizardRef.current?.startWebSocketListening(),
      cancelWebSocketListening: () => wizardRef.current?.cancelWebSocketListening(),
    });

  useImperativeHandle(ref, () => ({
    prev: () => wizardRef.current?.prev(),
    goToStep: (index: number) => wizardRef.current?.goToStep(index),
    startWebSocketListening: () => wizardRef.current?.startWebSocketListening(),
    cancelWebSocketListening: () => wizardRef.current?.cancelWebSocketListening(),
  }));

  const handleStepChange = (prevStep: number, nextStep: number) => {
    setCurrentStep(nextStep);
    onStepChange?.(prevStep, nextStep);
  };

  return (
    <FlowWizard
      ref={wizardRef}
      steps={steps}
      onFinish={handleFinish}
      onStepChange={handleStepChange}
      animationType="scale"
      initialStep={resolvedInitialStep}
      layout="vertical"
      nextText="measurement.startMeasurement"
      customButtons={{
        backButton,
        finishButton,
        customAction,
      }}
      webSocketConfig={webSocketConfig}
      onWaitingStateChange={onWaitingStateChange}
    />
  );
});

export default MeasurementFlow;
